import { z } from 'zod';
import { IsoTimestamp, UserId } from './common';

/**
 * Mirrors the shape of data/defaults.json from the personal-use codebase.
 * Each section is a free-form key/string map so users can add fields the
 * code doesn't know about, and the LLM answerer can still reference them.
 */
export const AnswerSection = z.record(z.string(), z.string());
export type AnswerSection = z.infer<typeof AnswerSection>;

/**
 * Provenance of a `CustomAnswer` — drives the UI badge in mobile +
 * desktop preferences screens. See #290.
 *
 *   - `'user'`        — typed directly by the user in mobile/desktop
 *                       settings, OR auto-promoted from `'auto-*'`
 *                       once the user edits the entry (the act of
 *                       editing claims ownership).
 *   - `'auto-human'`  — captured by the worker's Tier-5 tray prompt
 *                       (user typed it mid-cycle, on a different
 *                       surface than the settings UI).
 *   - `'auto-llm'`    — promoted by the worker after a SUCCESSFUL
 *                       application that used an LLM-resolved answer
 *                       for this field. Gated on `counters.applied++`
 *                       per #288 so failed-submission answers don't
 *                       poison the bank.
 */
const CUSTOM_ANSWER_SOURCE_VALUES = ['user', 'auto-human', 'auto-llm'] as const;
export const CustomAnswerSource = z.enum(CUSTOM_ANSWER_SOURCE_VALUES);
export type CustomAnswerSource = z.infer<typeof CustomAnswerSource>;

/** Forward-compat read variant — see #482. */
export const CustomAnswerSourceReadable = z
  .enum([...CUSTOM_ANSWER_SOURCE_VALUES, 'unknown'] as const)
  .catch('unknown');
export type CustomAnswerSourceReadable = z.infer<typeof CustomAnswerSourceReadable>;

/**
 * Free-form Q/A pair the user adds when a real Easy Apply form shows a
 * question that isn't in any of the default sections. The optional
 * `category` helps the LLM match similar phrasings on future forms.
 */
export const CustomAnswer = z.object({
  // Real-world prompts (especially "describe a time when…" essay questions
  // on Workday + Greenhouse forms) routinely run past 500 chars. Cap at
  // 1500 to swallow the long-tail without losing the abuse ceiling — every
  // schema-bound input still has a hard upper bound.
  question: z.string().min(1).max(1500),
  answer: z.string().max(2000).default(''),
  category: z.string().max(100).optional(),
  /**
   * Provenance. Defaults to `'user'` so existing pre-#290 entries
   * (which have no `source` field) parse cleanly with the right
   * implicit semantics — they WERE user-typed; that's the only path
   * that existed before the worker started promoting.
   */
  source: CustomAnswerSourceReadable.default('user'),
  /**
   * When the worker first promoted this entry. Absent for `'user'`-
   * sourced entries (the bank's top-level `updatedAt` tracks user
   * edits separately). Present for `'auto-*'` entries so the UI can
   * show "learned on Jun 25, 2026" or similar.
   */
  firstSeen: IsoTimestamp.optional(),
});
export type CustomAnswer = z.infer<typeof CustomAnswer>;

export const AnswerBank = z.object({
  userId: UserId,
  personal: AnswerSection.default({}),
  employment: AnswerSection.default({}),
  experience: AnswerSection.default({}),
  general: AnswerSection.default({}),
  custom: z.array(CustomAnswer).default([]),
  updatedAt: IsoTimestamp,
});

export type AnswerBank = z.infer<typeof AnswerBank>;

/**
 * `custom[].source` is overridden to strict `CustomAnswerSource` on the
 * write path so a client can't smuggle 'unknown' into DDB via a
 * PATCH — see #482.
 */
const CustomAnswerStrict = z.object({
  question: z.string().min(1).max(1500),
  answer: z.string().max(2000).default(''),
  category: z.string().max(100).optional(),
  source: CustomAnswerSource.default('user'),
  firstSeen: IsoTimestamp.optional(),
});

export const UpdateAnswerBankRequest = AnswerBank.omit({
  userId: true,
  updatedAt: true,
})
  .extend({
    custom: z.array(CustomAnswerStrict),
  })
  .partial();

export type UpdateAnswerBankRequest = z.infer<typeof UpdateAnswerBankRequest>;
